/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import { errorToast, successToast, useOutsideClick } from '../../../utils/helper';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from '../../../utils/constants';
import { Helmet } from 'react-helmet';
import Table from '../../../component/common/Table';
import Pagination from '../../../component/common/Pagination/Pagination';
import { Description, Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react';
import { Api } from '../../../api';
import { CommonTable } from '../../../component/common/table2/CommonTable';
import moment from 'moment';
import { SpecialistPageData } from '../../../utils/pageContent';
import { CompanyApi } from '../../../api/companyApi';
import {
  EnvelopeIcon,
  DocumentTextIcon,
  XMarkIcon,
  EyeIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import CommonPopover from '../../../component/common/popover';

const initialComplaintsData = SpecialistPageData.complaintTabData.tableData;

function Complaint() {
  const [activeTab, setActiveTab] = useState('All');
  const wrapperRef = useRef(null);
  const [complainsList, setComplaintsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tab, setTab] = useState(1);
  const [openComplaintModal, setOpenComplaintModal] = useState(false);
  const pageData = SpecialistPageData.complaintTabData;
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvedComplainsCount, setResolvedComplaintsCount] = useState();
  const [unresolvedComplainsCount, setUnResolvedComplaintsCount] = useState();
  const [showLoading, setShowLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [noteMessage, setNoteMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [viewNotesModal, setViewNotesModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { complaint, action: 'resolve'/'unresolve' }

  const fetchData = async (page = DEFAULT_PAGE, search = searchTerm) => {
    try {
      setLoading(true);
      const response = await Api.getComplaintList(
        page,
        DEFAULT_PAGE_SIZE,
        activeTab === 'Resolved' ? 2 : activeTab === 'Unresolved' ? 1 : 'All',
        search
      );
      if (response?.data?.meta?.code === 200) {
        console.log('API Response:', {
          dataLength: response.data.data.length,
          total: response.data.meta.total,
          pageSize: DEFAULT_PAGE_SIZE,
          page: page,
          activeTab: activeTab,
          statusFilter: activeTab === 'Resolved' ? 2 : activeTab === 'Unresolved' ? 1 : 'All',
          statusCounts: response?.data?.meta?.statusCounts,
          rawData: response.data.data.map((item) => ({ id: item._id, status: item.status }))
        });
        setResolvedComplaintsCount(response?.data?.meta?.statusCounts?.status2);
        setUnResolvedComplaintsCount(response?.data?.meta?.statusCounts?.status1);
        setComplaintsList(response.data.data);
        setTotalCount(response.data.meta.total);
      }
    } catch (error) {
      console.error('🚀 ~ Fetching Data ~ error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, activeTab]);

  // Handle search with debouncing
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchData(1, searchTerm);
      setCurrentPage(1);
    }, 500); // 500ms delay

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const handleSendEmail = (complaint) => {
    setSelectedComplaint(complaint);
    setSendEmailModal(true);
  };

  const handleAddNote = (complaint) => {
    setSelectedComplaint(complaint);
    setAddNoteModal(true);
  };

  const handleViewNotes = (complaint) => {
    setSelectedComplaint(complaint);
    setViewNotesModal(true);
  };

  const handleSendEmailSubmit = async () => {
    if (emailLoading) return;

    try {
      setEmailLoading(true);
      const emailData = {
        id: selectedComplaint?.id || selectedComplaint?._id,
        sendEmail: true,
        emailMessage: emailMessage
      };

      const response = await Api.updateComplaintStatus(emailData);

      if (response?.data?.meta?.code === 200) {
        successToast('Email sent successfully!');
        // Refresh the complaints list to get updated data
        fetchData(currentPage);
      } else {
        errorToast('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      errorToast('Failed to send email. Please try again.');
    } finally {
      setEmailLoading(false);
    }

    // Reset form and close modal
    setEmailMessage('');
    setSendEmailModal(false);
    setSelectedComplaint(null);
  };

  const handleAddNoteSubmit = async () => {
    if (noteLoading) return;

    try {
      setNoteLoading(true);
      const noteData = {
        id: selectedComplaint?.id || selectedComplaint?._id,
        adminNote: noteMessage
      };

      const response = await Api.updateComplaintStatus(noteData);

      if (response?.data?.meta?.code === 200) {
        successToast('Note added successfully!');
        // Refresh the complaints list to get updated data
        fetchData(currentPage);
      } else {
        errorToast('Failed to add note. Please try again.');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      errorToast('Failed to add note. Please try again.');
    } finally {
      setNoteLoading(false);
    }

    // Reset form and close modal
    setNoteMessage('');
    setAddNoteModal(false);
    setSelectedComplaint(null);
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /* COLUMNS */
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'User name and email',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal text-black dark:text-white">
                {row?.original?.user_id?.name}
              </div>
              <div className="text-xs font-normal text-gray-700 dark:text-white">
                {row?.original?.user_id?.email}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'complaint',
        header: 'Complaint',
        cell: ({ row }) => {
          return (
            <div
              className="w-[250px] text-sm font-normal text-black dark:text-white"
              style={{ overflowWrap: 'anywhere' }}
            >
              {row?.original?.complaint}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'complaint_against',
        header: 'Complaint against',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal text-black dark:text-white">
                {row?.original?.session_id?.doctor_user_id?.first_name}{' '}
                {row?.original?.session_id?.doctor_user_id?.last_name}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'complain_date',
        header: 'Date',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal capitalize text-black dark:text-white">
                {moment(row?.original?.createdAt).format('DD.MM.YYYY')}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'complaint_type',
        header: 'Complaint Type',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal capitalize text-black dark:text-white">
                {row?.original?.complaint_type}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'urgency_level',
        header: 'Urgency Level',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal capitalize text-black dark:text-white">
                {row?.original?.urgency_level}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'attachment',
        header: 'Attachment',
        cell: ({ row }) => {
          return (
            <div className="text-left">
              <div className="text-sm font-normal capitalize text-black dark:text-white">
                {row?.original?.file_link ? (
                  <a
                    href={row?.original?.file_link}
                    download
                    target="blank"
                    title={row?.original?.file_name}
                    className="hover:text-shoorah-secondary"
                  >
                    <div className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {row?.original?.file_name}
                    </div>
                  </a>
                ) : (
                  'No attachment'
                )}
              </div>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      },

      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const complaint = row?.original;
          const hasNotes = complaint?.admin_notes && complaint?.admin_notes.length > 0;
          const isResolved = complaint?.status === 2;
          const isClosed = complaint?.status === 3;

          return (
            <div className="text-center">
              <CommonPopover
                label={
                  <>
                    <span>Select</span>
                    <ChevronDownIcon className="w-4 h-4 ml-1" />
                  </>
                }
                className="w-[100px] gap-2 text-xs py-2 rounded-md text-black dark:text-white border-gray-300 dark:border-gray-600"
                contentClassName="w-[180px] rounded-lg mt-1 px-0 py-2"
              >
                <div className="text-sm">
                  {/* Send Email */}
                  <button
                    onClick={() => handleSendEmail(complaint)}
                    disabled={loading || showLoading || emailLoading || noteLoading}
                    className="px-4 py-2 outline-none text-sm w-full text-left hover:text-shoorah-secondary hover:bg-[#E5EAF966] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <EnvelopeIcon className="h-4 w-4" />
                    Send Email
                  </button>

                  {/* View Notes - only show if notes exist */}
                  {hasNotes && (
                    <button
                      onClick={() => handleViewNotes(complaint)}
                      disabled={loading || showLoading || emailLoading || noteLoading}
                      className="px-4 py-2 outline-none text-sm w-full text-left hover:text-shoorah-secondary hover:bg-[#E5EAF966] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Notes
                    </button>
                  )}

                  {/* Add Note */}
                  <button
                    onClick={() => handleAddNote(complaint)}
                    disabled={loading || showLoading || emailLoading || noteLoading}
                    className="px-4 py-2 outline-none text-sm w-full text-left hover:text-shoorah-secondary hover:bg-[#E5EAF966] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                    Add Note
                  </button>

                  {/* Resolved/Unresolved toggle - hide if closed */}
                  {!isClosed && (
                    <>
                      {isResolved ? (
                        <button
                          onClick={() => handleMarkAsUnresolved(complaint)}
                          disabled={loading || showLoading || emailLoading || noteLoading}
                          className="px-4 py-2 outline-none text-sm w-full text-left hover:text-shoorah-secondary hover:bg-[#E5EAF966] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          Mark as Unresolved
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkAsResolved(complaint)}
                          disabled={loading || showLoading || emailLoading || noteLoading}
                          className="px-4 py-2 outline-none text-sm w-full text-left hover:text-shoorah-secondary hover:bg-[#E5EAF966] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Mark as Resolved
                        </button>
                      )}
                    </>
                  )}

                  {/* Closed status indicator */}
                  {isClosed && (
                    <div className="px-4 py-2 text-sm text-red-500 flex items-center gap-2">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
                        />
                      </svg>
                      Closed
                    </div>
                  )}
                </div>
              </CommonPopover>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [complainsList]
  );

  /* MARK AS UNRESOLVED FUNCTION */
  const handleMarkAsUnresolved = (complaint) => {
    setPendingAction({ complaint, action: 'unresolve' });
    setConfirmationModal(true);
  };

  /* MARK AS RESOLVED FUNCTION */
  const handleMarkAsResolved = (complaint) => {
    setPendingAction({ complaint, action: 'resolve' });
    setConfirmationModal(true);
  };

  /* HANDLE CONFIRMATION ACTION */
  const handleConfirmAction = async () => {
    if (!pendingAction) return;

    setShowLoading(true);
    try {
      const data = {
        id: pendingAction.complaint._id || pendingAction.complaint.id,
        status: pendingAction.action === 'resolve' ? 2 : 1
      };

      const response = await Api.updateComplaintStatus(data);
      if (response?.data?.meta?.code === 200) {
        successToast(
          `The complaint has been marked as ${
            pendingAction.action === 'resolve' ? 'resolved' : 'unresolved'
          }`
        );
        fetchData(currentPage);
      } else {
        errorToast(response?.data?.meta?.message);
      }
    } catch (error) {
      console.log('🚀 ~ handleConfirmAction ~ error:', error);
      errorToast(
        `Failed to mark complaint as ${
          pendingAction.action === 'resolve' ? 'resolved' : 'unresolved'
        }`
      );
    } finally {
      setShowLoading(false);
      setConfirmationModal(false);
      setPendingAction(null);
    }
  };

  return (
    <div className="relative">
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@500&family=Ubuntu&family=Work+Sans:ital,wght@0,100..900;1,100..900&display=swap"
          rel="stylesheet"
        />
        <meta charSet="utf-8" />
        <title>{pageData.metaTitle}</title>
      </Helmet>

      {openComplaintModal && selectedComplaint ? (
        <Dialog
          open={openComplaintModal}
          onClose={() => {
            setOpenComplaintModal(false);
            setSelectedComplaint(null);
          }}
          className="relative z-50"
        >
          <DialogPanel>
            <div className="fixed inset-0 z-40 flex w-screen items-center justify-center bg-black/25">
              <div className="w-[95vw] overflow-hidden rounded-2xl border border-[#F1F2F4] bg-white shadow-md dark:border-shoorah-darkBorder dark:bg-shoorah-darkBgColor dark:text-white lg:w-[40vw] p-10">
                {/* HEADING */}
                <div className="items-left flex w-full gap-[1rem]">
                  <div
                    className={
                      tab === 1
                        ? `items-left ease flex w-full cursor-pointer justify-between font-medium duration-1000`
                        : `flex w-full cursor-pointer items-center justify-between py-3 font-medium`
                    }
                  >
                    <DialogTitle>
                      <div className="hover:shoorah-primary items-center border-none text-xl font-bold text-shoorah-lightBlack dark:text-shoorah-darkBg focus:outline-none">
                        {pageData.complaintModalData.complaintText}{' '}
                      </div>
                    </DialogTitle>
                  </div>
                </div>

                {/* BODY */}
                <div className="mt-5">
                  <div className="flex items-center gap-2">
                    <p className="text-gray-400 text-base font-normal">User name:</p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.user_id?.name}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <p className="text-gray-400 text-base font-normal">User email address:</p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.user_id?.email}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <p className="text-gray-400 text-base font-normal">Complaint against:</p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.session_id?.doctor_user_id?.first_name}{' '}
                      {selectedComplaint?.session_id?.doctor_user_id?.last_name}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center flex-wrap gap-2">
                    <p className="text-gray-400 text-base font-normal flex-shrink-0">
                      Complaint Description:
                    </p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.complaint}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <p className="text-gray-400 text-base font-normal">Complaint type:</p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.complaint_type}{' '}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <p className="text-gray-400 text-base font-normal">Urgency Level:</p>
                    <p className="text-black text-base font-normal">
                      {selectedComplaint?.urgency_level}{' '}
                    </p>
                  </div>
                </div>

                <div className="text-medium flex justify-end pt-5">
                  <button
                    onClick={() => {
                      setOpenComplaintModal(false);
                      handleMarkAsResolved(selectedComplaint);
                    }}
                    className="btn-primary mt-4"
                    style={{ borderRadius: '50px' }}
                  >
                    {pageData.complaintModalData.respondViaEmailText}
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      ) : null}

      <div className="mt-5 p-6 bg-white rounded-2xl dark:border-shoorah-darkBorder border-[1.5px] dark:bg-shoorah-darkBgTabColor border-shoorah-borderGray">
        <div className="flex items-center gap-3">
          <div
            onClick={() => !loading && setActiveTab('All')}
            className={classNames(
              activeTab === 'All' ? 'border-black text-black dark:text-white' : 'border-gray-200',
              'flex items-center gap-2 cursor-pointer hover:text-gray-600 border rounded-full px-4 py-2 text-sm',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            )}
          >
            {pageData.tabList.all}{' '}
            <span className="px-3 py-1 text-xs font-medium bg-[#F1F1F1] rounded-full text-shoorah-primary">
              {totalCount}
            </span>{' '}
          </div>
          <div
            onClick={() => !loading && setActiveTab('Resolved')}
            className={classNames(
              activeTab === 'Resolved'
                ? 'border-black text-black dark:text-white'
                : 'border-gray-200',
              'flex items-center gap-2 cursor-pointer hover:text-gray-600 border rounded-full px-4 py-2 text-sm',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            )}
          >
            {pageData.tabList.resolved}{' '}
            <span className="px-3 py-1 text-xs font-medium bg-[#DDF2BF] rounded-full text-[#438038]">
              {resolvedComplainsCount}
            </span>{' '}
          </div>
          <div
            onClick={() => !loading && setActiveTab('Unresolved')}
            className={classNames(
              activeTab === 'Unresolved'
                ? 'border-black text-black dark:text-white'
                : 'border-gray-200',
              'flex items-center gap-2 cursor-pointer hover:text-gray-600 border rounded-full px-4 py-2 text-sm',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            )}
          >
            {pageData.tabList.unresolved}{' '}
            <span className="px-3 py-1 text-xs font-medium bg-[#FFFDD2] rounded-full text-[#CCB014]">
              {unresolvedComplainsCount}
            </span>{' '}
          </div>
        </div>

        {/* Search Input */}
        <div className="mt-4 mb-4">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search by user name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 text-sm focus:border-shoorah-primary focus:outline-none focus:ring-1 focus:ring-shoorah-primary dark:border-gray-600 dark:bg-shoorah-darkBgTabColor dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-shoorah-primary"></div>
                <span className="text-sm">Loading complaints...</span>
              </div>
            </div>
          ) : (
            <CommonTable
              data={
                activeTab === 'Resolved'
                  ? complainsList?.filter((item) => item?.status === 2)
                  : activeTab === 'Unresolved'
                  ? complainsList?.filter((item) => item?.status === 1)
                  : complainsList || []
              }
              columns={columns}
              noExtraRow
            />
          )}
        </div>
        <div>
          {totalCount > DEFAULT_PAGE_SIZE ? (
            <Pagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={handlePageChange}
            />
          ) : null}
        </div>
      </div>

      {/* Send Email Dialog */}
      {sendEmailModal && (
        <Dialog
          open={sendEmailModal}
          onClose={() => {
            setSendEmailModal(false);
            setEmailMessage('');
            setSelectedComplaint(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-shoorah-darkBgColor">
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">
                  Send Email to User
                </DialogTitle>
                <button
                  onClick={() => {
                    setSendEmailModal(false);
                    setEmailMessage('');
                    setSelectedComplaint(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Sending to:{' '}
                  <span className="font-medium">{selectedComplaint?.user_id?.email}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Regarding complaint: <span className="font-medium">#{selectedComplaint?.id}</span>
                </p>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="email-message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="email-message"
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-shoorah-primary focus:outline-none focus:ring-1 focus:ring-shoorah-primary dark:border-gray-600 dark:bg-shoorah-darkBgTabColor dark:text-white"
                  placeholder="Write your message to the user..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSendEmailModal(false);
                    setEmailMessage('');
                    setSelectedComplaint(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmailSubmit}
                  disabled={!emailMessage.trim() || emailLoading}
                  className="btn-primary"
                >
                  {emailLoading ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}

      {/* Add Note Dialog */}
      {addNoteModal && (
        <Dialog
          open={addNoteModal}
          onClose={() => {
            setAddNoteModal(false);
            setNoteMessage('');
            setSelectedComplaint(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-shoorah-darkBgColor">
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">
                  Add Note
                </DialogTitle>
                <button
                  onClick={() => {
                    setAddNoteModal(false);
                    setNoteMessage('');
                    setSelectedComplaint(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Complaint from:{' '}
                  <span className="font-medium">{selectedComplaint?.user_id?.email}</span>
                </p>
              </div>

              {/* Show existing notes if any */}
              {selectedComplaint?.admin_notes && selectedComplaint.admin_notes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Previous Notes:
                  </h4>
                  <div className="max-h-32 overflow-y-auto space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                    {selectedComplaint.admin_notes.map((note, index) => (
                      <div key={index} className="text-xs border-l-2 border-gray-300 pl-2">
                        <p className="text-gray-700 dark:text-gray-300">{note.note}</p>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                          By: {note.admin?.name || note.admin?.first_name || 'Admin'} •{' '}
                          {new Date(note.created_at).toLocaleDateString()}{' '}
                          {new Date(note.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label
                  htmlFor="note-message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Add New Note
                </label>
                <textarea
                  id="note-message"
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-shoorah-primary focus:outline-none focus:ring-1 focus:ring-shoorah-primary dark:border-gray-600 dark:bg-shoorah-darkBgTabColor dark:text-white"
                  placeholder="Add a private note for yourself..."
                  value={noteMessage}
                  onChange={(e) => setNoteMessage(e.target.value)}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setAddNoteModal(false);
                    setNoteMessage('');
                    setSelectedComplaint(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNoteSubmit}
                  disabled={!noteMessage.trim() || noteLoading}
                  className="btn-primary"
                >
                  {noteLoading ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}

      {/* View Notes Modal */}
      {viewNotesModal && (
        <Dialog
          open={viewNotesModal}
          onClose={() => {
            setViewNotesModal(false);
            setSelectedComplaint(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-shoorah-darkBgColor">
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white">
                  Admin Notes
                </DialogTitle>
                <button
                  onClick={() => {
                    setViewNotesModal(false);
                    setSelectedComplaint(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Complaint from:{' '}
                  <span className="font-medium">{selectedComplaint?.user_id?.email}</span>
                </p>
              </div>

              {/* Display all notes */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  All Notes ({selectedComplaint?.admin_notes?.length || 0}):
                </h4>
                <div className="max-h-80 overflow-y-auto space-y-3">
                  {selectedComplaint?.admin_notes && selectedComplaint.admin_notes.length > 0 ? (
                    selectedComplaint.admin_notes
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Show newest first
                      .map((note, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border-l-4 border-blue-400"
                        >
                          <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                            {note.note}
                          </p>
                          <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                            <span>By: {note.admin?.name || note.admin?.first_name || 'Admin'}</span>
                            <span>
                              {new Date(note.created_at).toLocaleDateString()}{' '}
                              {new Date(note.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      ))
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                      No notes found
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setViewNotesModal(false);
                    setAddNoteModal(true);
                  }}
                  className="btn-primary"
                >
                  Add New Note
                </button>
                <button
                  onClick={() => {
                    setViewNotesModal(false);
                    setSelectedComplaint(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}

      {/* Confirmation Modal */}
      {confirmationModal && pendingAction && (
        <Dialog
          open={confirmationModal}
          onClose={() => {
            setConfirmationModal(false);
            setPendingAction(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/25" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-shoorah-darkBgColor">
              <div className="mb-4">
                <DialogTitle className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Are you sure?
                </DialogTitle>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Are you sure you want to mark this complaint as{' '}
                  {pendingAction.action === 'resolve' ? 'resolved' : 'unresolved'}?
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setConfirmationModal(false);
                    setPendingAction(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={showLoading}
                  className="btn-primary"
                >
                  {showLoading ? 'Please wait...' : 'Sure'}
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default Complaint;
